/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Link,
  Chip,
  Tooltip,
  LinearProgress,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Divider,
  TextField,
  CircularProgress,
} from "@mui/material";
import { getGridStringOperators } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import FilterListIcon from "@mui/icons-material/FilterList";
import CodeIcon from "@mui/icons-material/Code";
import DualPaneDataGrid from "./DualPaneDataGrid";
import SolutionPreviewPanel from './SolutionPreviewPanel';
import CodeTooltip from './CodeTooltip';
import { fetchSubmission, ctRecordExportActivity } from "../../services/api";
import {
  INDIGO,
  INDIGO_LIGHT,
  VIOLET,
  TOOLBAR_BG_DARK,
  TOOLBAR_BG_LIGHT,
} from "../../utils/colors";

const diffColor = (d) => {
  if (d === "Easy") return "success";
  if (d === "Medium") return "warning";
  if (d === "Hard") return "error";
  return "default";
};

const normalizeText = (value) => String(value || "").toLowerCase();

const extractSelectedRowId = (model) => {
  if (!model) return null;

  if (Array.isArray(model)) {
    return model[0] ?? null;
  }

  if (model?.ids && typeof model.ids.values === "function") {
    return model.ids.values().next().value ?? null;
  }

  return null;
};

// ── CSV Export helper ────────────────────────────────────────────────────────
const exportCSV = (rows, problems) => {
  const headers = [
    "ID",
    "Name",
    "LeetCode Username",
    ...problems.map((p) => p.title || p.slug),
    "Assignment Solved",
    "Total",
    "Progress %",
  ];

  const csvRows = rows.map((row) => [
    row.userId,
    row.name,
    row.leetcodeUsername,
    ...problems.map((p) => (row[`slug_${p.slug}`] ? "Yes" : "No")),
    row.solved,
    row.total,
    row.total === 0 ? 0 : Math.round((row.solved / row.total) * 100),
  ]);

  const csv = [headers, ...csvRows]
    .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "progress.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// ── Standalone Toolbar (rendered outside DataGrid) ───────────────────────────
const CustomToolbar = ({
  problems,
  rows,
  diffFilter,
  setDiffFilter,
  progressFilter,
  setProgressFilter,
  searchText = "",
  onSearchChange,
  batchId = null,
}) => (
  <Box
    sx={{
      display: "flex",
      gap: 1,
      flexWrap: "wrap",
      alignItems: "center",
      py: 1.5,
      px: 2,
      borderBottom: 1,
      borderColor: "divider",
      paddingTop: 2,
      paddingBottom: 2,
      minHeight: "68px",
      background: (theme) =>
        theme.palette.mode === "dark" ? TOOLBAR_BG_DARK : TOOLBAR_BG_LIGHT,
    }}
  >
    <FilterListIcon fontSize="small" sx={{ color: INDIGO }} />
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      flex={1}
    >
      {onSearchChange && (
        <TextField
          size="small"
          placeholder="Search students…"
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      )}

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Difficulty</InputLabel>
        <Select
          value={diffFilter}
          label="Difficulty"
          onChange={(e) => setDiffFilter(e.target.value)}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Easy">Easy</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="Hard">Hard</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Student Progress</InputLabel>
        <Select
          value={progressFilter}
          label="Student Progress"
          onChange={(e) => setProgressFilter(e.target.value)}
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Complete">100% Complete</MenuItem>
          <MenuItem value="Incomplete">Incomplete</MenuItem>
          <MenuItem value="NotStarted">Not Started</MenuItem>
        </Select>
      </FormControl>
    </Stack>

    <Divider orientation="vertical" flexItem />

    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={() => {
          exportCSV(rows, problems);
          if (batchId) {
            ctRecordExportActivity(batchId).catch(() => {});
          }
        }}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 2,
          borderColor: INDIGO,
          color: INDIGO,
          "&:hover": { bgcolor: INDIGO_LIGHT },
        }}
      >
        Export CSV
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PrintIcon />}
        onClick={() => window.print()}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 2,
          borderColor: VIOLET,
          color: VIOLET,
          "&:hover": { bgcolor: INDIGO_LIGHT },
        }}
      >
        Print
      </Button>
    </Stack>
  </Box>
);


// ── Main Component ────────────────────────────────────────────────────────────
const ProgressTable = ({ students = [], problems = [], progressMap = {}, batchId = null }) => {
  const [diffFilter, setDiffFilter] = useState("All");
  const [progressFilter, setProgressFilter] = useState("All");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [aiPlagiarism, setAiPlagiarism] = useState(null);
  const [peerPlagiarism, setPeerPlagiarism] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });


  const [panelOpen, setPanelOpen] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loadingCell, setLoadingCell] = useState(null); // `${studentId}-${problemSlug}`
  const [cellError, setCellError] = useState(null);



  // Hover state: { anchorEl, cellKey, code, language, problemTitle, studentName }
  const [hoverState, setHoverState] = useState(null);
  const hoverTimerRef = React.useRef(null);

  const handleCellMouseEnter = (e, cellKey, studentId, studentName, problemSlug, problemTitle) => {
    // Only show tooltip if we already have submission data cached
    const cached = submissionCache.current[cellKey];
    if (!cached) return;

    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHoverState({
        anchorEl: e.currentTarget,
        cellKey,
        code:         cached.code,
        language:     cached.language,
        problemTitle,
        studentName,
      });
    }, 300);
  };

  const handleCellMouseLeave = () => {
    clearTimeout(hoverTimerRef.current);
    setHoverState(null);
  };

  // Cache fetched submissions so the tooltip can show without re-fetching
  const submissionCache  = React.useRef({});

  const handleSolvedClick = async (leetcodeUsername, studentName, problemSlug, problemTitle, value) => {
    const cellKey = `${leetcodeUsername}-${problemSlug}`;
    setCellError(null);

    // Use cache if available — no API call needed
    const cached = submissionCache.current[cellKey];
    if (cached) {
      setSelectedSubmission({
        problemTitle: problemTitle ?? 'Unknown Problem',
        studentName,
        studentId:    leetcodeUsername,
        problemSlug:  problemSlug,
        status: value,
        language: cached.language ?? null,
        runtime:  cached.runtime  ?? null,
        memory:   cached.memory   ?? null,
        code:     cached.code     ?? '',
      });
      setPanelOpen(true);
      return;
    }

    // Not cached — fetch from API
    setLoadingCell(cellKey);
    setPanelLoading(true);

    try {
      const response = await fetchSubmission(leetcodeUsername, problemSlug);
      console.log("API, response",response)
      setAiPlagiarism(response.data?.plagiarism ?? null);
      setPeerPlagiarism(response.data?.peerPlagiarism ?? null);
      // API returns ALL submissions for the student at once.
      // Cache each one individually by titleSlug so subsequent clicks on
      // other problem cells don't need a separate round-trip.
      const allSubs = response.data?.data?.submissions ?? [];
      for (const sub of allSubs) {
        if (!sub?.titleSlug) continue;
        const key = `${leetcodeUsername}-${sub.titleSlug}`;
        // Prefer an entry that actually has code; skip overwriting a good
        // cached entry with an empty one.
        if (!submissionCache.current[key] || sub.code?.trim()) {
          submissionCache.current[key] = sub;
        }
      }

      // Look up the specific problem the student clicked.
      const data = submissionCache.current[cellKey] ?? null;

      console.log("Data", data)

      setSelectedSubmission({
        problemTitle: problemTitle ?? 'Unknown Problem',
        studentName,
        studentId:    leetcodeUsername,
        problemSlug:  problemSlug,
        status: value,
        language: data?.language ?? null,
        runtime:  data?.runtime  ?? null,
        memory:   data?.memory   ?? null,
        code:     data?.code     ?? '',
      });
      setPanelOpen(true);
    } catch (err) {
      console.error('[ProgressTable] fetchSubmission error:', err);
      setCellError(cellKey);
    } finally {
      setLoadingCell(null);
      setPanelLoading(false);
    }
  };

  const stringOperators = getGridStringOperators();
  const containsOperator = stringOperators.find(
    (operator) => operator.value === "contains",
  );

  const studentFilterOperators = useMemo(() => {
    const buildContainsOperator = (label, getter) => ({
      label,
      value: label,
      InputComponent: containsOperator?.InputComponent,
      InputComponentProps: containsOperator?.InputComponentProps,
      getApplyFilterFn: (filterItem) => {
        const query = normalizeText(filterItem?.value).trim();
        if (!query) return null;
        return (_value, row) => normalizeText(getter(row)).includes(query);
      },
    });

    return [
      buildContainsOperator("Name contains", (row) => row.name),
      buildContainsOperator("Roll Number contains", (row) => row.rollNumber),
      buildContainsOperator(
        "LeetCode Username contains",
        (row) => row.leetcodeUsername,
      ),
    ];
  }, [containsOperator]);

  // ── Filtered problems (for columns) ─────────────────────────────────────
  const filteredProblems = useMemo(
    () =>
      diffFilter === "All"
        ? problems
        : problems.filter((p) => p.difficulty === diffFilter),
    [problems, diffFilter],
  );

  // ── All rows (unfiltered by progress) ───────────────────────────────────
  const allRows = useMemo(
    () =>
      students.map((student) => {
        const uid = student.userId?.toString();
        const mongoId = student._id?.toString();

        // Try both userId and _id as keys, in case API uses either
        const solvedMap = progressMap[uid] || progressMap[mongoId] || {};

        const solved = filteredProblems.filter((p) => solvedMap[p.slug]).length;
        const total = filteredProblems.length;

        const slugFields = Object.fromEntries(
          problems.map((p) => [`slug_${p.slug}`, !!solvedMap[p.slug]]),
        );

        return {
          id: student._id,
          userId: student.userId,
          rollNumber: student.rollNumber || student.userId,
          name: student.name,
          leetcodeUsername: student.leetcodeUsername,
          solved,
          total,
          ...slugFields,
        };
      }),
    [students, problems, filteredProblems, progressMap],
  );

  // ── Filtered rows (for progress filter) ─────────────────────────────────
  const rows = useMemo(() => {
    if (progressFilter === "All") return allRows;
    if (progressFilter === "Complete")
      return allRows.filter((r) => r.total > 0 && r.solved === r.total);
    if (progressFilter === "Incomplete")
      return allRows.filter((r) => r.solved > 0 && r.solved < r.total);
    if (progressFilter === "NotStarted")
      return allRows.filter((r) => r.solved === 0);
    return allRows;
  }, [allRows, progressFilter]);

  // ── Search-filtered rows ─────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        normalizeText(r.name).includes(q) ||
        normalizeText(r.rollNumber).includes(q) ||
        normalizeText(r.userId).includes(q) ||
        normalizeText(r.leetcodeUsername).includes(q),
    );
  }, [rows, searchText]);

  const problemSolvers = (slug) =>
    students.filter((s) => progressMap[s._id?.toString()]?.[slug]).length;

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        field: "student",
        headerName: "Student",
        minWidth: 180,
        width: 260,
        sortable: true,
        filterOperators: studentFilterOperators,
        valueGetter: (value, row) => row.name,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              justifyContent: "center",
              pt: 0,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {row.rollNumber || row.userId} - {row.name}
            </Typography>
            <Link
              href={`https://leetcode.com/u/${row.leetcodeUsername}/`}
              target="_blank"
              rel="noreferrer"
              variant="caption"
              underline="hover"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Tooltip title={`Leetcode@${row.leetcodeUsername}`}>
                <CodeIcon fontSize="small" />
              </Tooltip>
            </Link>
          </Box>
        ),
      },
      {
        field: "progress",
        headerName: "Progress",
        minWidth: 120,
        width: 140,
        align: "center",
        headerAlign: "center",
        sortable: true,
        valueGetter: (value, row) => row.solved,
        renderCell: ({ row }) => {
          const pct =
            row.total === 0 ? 0 : Math.round((row.solved / row.total) * 100);
          return (
            <Box sx={{ width: "100%", px: 1 }}>
              <Typography variant="caption" fontWeight={600}>
                {row.solved}/{row.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                color={pct === 100 ? "success.main" : "primary.main"}
              />
              <Typography variant="caption" color="text.secondary">
                {pct}%
              </Typography>
            </Box>
          );
        },
      },
      ...filteredProblems.map((p) => ({
        field: `slug_${p.slug}`,
        minWidth: 100,
        width: 110,
        align: "center",
        headerAlign: "center",
        sortable: true,
        valueGetter: (value, row) => !!row[`slug_${p.slug}`],
        renderHeader: () => (
          <Box sx={{ textAlign: "center", lineHeight: 1.3 }}>
            <Tooltip title={p.title || p.slug}>
              <Link
                href={p.link || `https://leetcode.com/problems/${p.slug}/`}
                target="_blank"
                rel="noreferrer"
                underline="hover"
                variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxWidth: 100,
                }}
              >
                {`${p.questionFrontendId || ""} - ${p.title || p.slug}`}
              </Link>
            </Tooltip>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 0.5,
                mt: 0.5,
              }}
            >
              {p.difficulty && (
                <Chip
                  label={p.difficulty.charAt(0)}
                  size="small"
                  color={diffColor(p.difficulty)}
                  sx={{ height: 18, fontSize: "0.65rem" }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {problemSolvers(p.slug)}/{students.length}
              </Typography>
            </Box>
          </Box>
        ),
        renderCell: ({ value, row }) => {
          const { name, leetcodeUsername } = row;
          const isSolved  = value === true;
          // Key is always username-based so handleSolvedClick and the hover
          // state share the same cache namespace.
          const cellKey   = `${leetcodeUsername}-${p.slug}`;
          const isLoading = loadingCell === cellKey;
          const hasError  = cellError   === cellKey;

          if (!isSolved) return <CloseIcon fontSize="small" color="disabled" />;

          if (isLoading) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <CircularProgress size={16} />
              </Box>
            );
          }

          return (
            <Box
              onMouseEnter={(e) => handleCellMouseEnter(e, cellKey, leetcodeUsername, name, p.slug, p.title)}
              onMouseLeave={handleCellMouseLeave}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                color: hasError ? 'error.main' : 'success.main',
                '&:hover': { color: hasError ? 'error.dark' : 'success.dark' },
                transition: 'color 0.15s',
              }}
            >
              <CheckIcon fontSize="small" />
            </Box>
          );
        },
      })),
    ],
    [filteredProblems, students, progressMap, studentFilterOperators, loadingCell, cellError],
  );

  // ── Split into frozen left column + scrollable right columns ─────────────
  const leftColumns = useMemo(() => columns.slice(0, 1), [columns]);
  const rightColumns = useMemo(() => columns.slice(1), [columns]);

  // ── Helper: reset pagination to page 0 on any filter change ──────────────
  const resetPage = () => setPaginationModel((p) => ({ ...p, page: 0 }));

  // ── Early returns (after all hooks) ─────────────────────────────────────
  if (!students.length)
    return (
      <Typography color="text.secondary">No students in this batch.</Typography>
    );
  if (!problems.length)
    return (
      <Typography color="text.secondary">
        No problems in this assignment.
      </Typography>
    );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @media print {
          body > *:not(.progress-table-print) { display: none !important; }
          .progress-table-print { display: block !important; }
          .MuiDataGrid-footerContainer,
          .MuiToolbar-root button { display: none !important; }
        }
      `}</style>

      {panelLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
          }}
        >
          <CircularProgress size={56} thickness={4} sx={{ color: VIOLET }} />
        </Box>
      )}

      <Box className="progress-table-print" sx={{ width: "100%" }}>
        <CustomToolbar
          problems={filteredProblems}
          rows={filteredRows}
          diffFilter={diffFilter}
          setDiffFilter={(val) => { setDiffFilter(val); resetPage(); }}
          progressFilter={progressFilter}
          setProgressFilter={(val) => { setProgressFilter(val); resetPage(); }}
          searchText={searchText}
          onSearchChange={(val) => { setSearchText(val); resetPage(); }}
          batchId={batchId}
        />
        <DualPaneDataGrid
          rows={filteredRows}
          leftColumns={leftColumns}
          rightColumns={rightColumns}
          leftWidth={280}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          rowSelectionModel={{
            type: "include",
            ids: new Set(selectedRowId ? [selectedRowId] : []),
          }}
          onRowSelectionModelChange={(model) =>
            setSelectedRowId(extractSelectedRowId(model))
            
          }
          onRowClick={(params) => setSelectedRowId(params.id ?? null)}
          onCellClick={(params, event) => {
            const { field } = params;
            if (!field.startsWith('slug_')) return;
            const slug = field.replace(/^slug_/, '');
            const cellValue = params.row[field];
            if (!cellValue) return;
            const lc = params.row.leetcodeUsername;
            if (!lc) return;
            event.defaultMuiPrevented = true;
            event.stopPropagation();
            const problem = filteredProblems.find((x) => x.slug === slug);
            setSelectedRowId(params.row.id ?? null);
            void handleSolvedClick(
              lc,
              params.row.name,
              slug,
              problem?.title,
              cellValue,
            );
          }}
          disableColumnSorting
        />
      </Box>

      <CodeTooltip
        anchorEl={hoverState?.anchorEl ?? null}
        open={!!hoverState}
        code={hoverState?.code ?? ''}
        language={hoverState?.language ?? ''}
        problemTitle={hoverState?.problemTitle ?? ''}
        studentName={hoverState?.studentName ?? ''}
      />

      <SolutionPreviewPanel
        open={panelOpen}
        onClose={() => {
          setPanelOpen(false);
          setSelectedSubmission(null);
        }}
        submission={selectedSubmission}
        loading={panelLoading}
        plagiarismReport={aiPlagiarism}
        peerRows={peerPlagiarism}
      />
    </>
  );
};

export default ProgressTable;
